import type { PublicRetroParticipant } from '@planning-session/shared';
import styles from './PresenceAvatars.module.css';

export interface PresenceAvatarsProps {
  readonly participants: readonly PublicRetroParticipant[];
}

const HUES = ['maroon', 'azure', 'orchid', 'navy', 'moss', 'grape'] as const;

// Deterministic, not random: the same person keeps the same hue across a reload,
// and everyone in the room derives the same hue for the same person independently.
function hueFor(id: string): (typeof HUES)[number] {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  const hue = HUES[hash % HUES.length];
  return hue ?? HUES[0];
}

function initial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

/**
 * Who is here, at a glance — the initial and the hue are a per-person handle,
 * not a state report, so this is scoped out of the State-Only Rule. See
 * DESIGN.md's "The Presence Hue Rule."
 */
export function PresenceAvatars({ participants }: PresenceAvatarsProps) {
  const present = participants.filter((participant) => participant.connected);
  if (present.length === 0) return null;

  return (
    <div className={styles.stack} role="group" aria-label="Who is here">
      {present.map((participant) => (
        <span key={participant.id} className={`${styles.avatar} ${styles[hueFor(participant.id)]}`} title={participant.name}>
          {initial(participant.name)}
        </span>
      ))}
    </div>
  );
}
